import React, { useState, useEffect } from 'react';
import { Navigation, Clock, Activity, MapPin, Play, Pause, RotateCcw } from 'lucide-react';
import './FleetCommandHeader.css';

const FleetCommandHeader = ({
  isAnimating,
  setIsAnimating,
  currentTime,
  mapMode,
  setMapMode,
  showHeatmap,
  setShowHeatmap,
  onStartAnimation,
  onPauseAnimation,
  onResetAnimation
}) => {
  const [statusMessage, setStatusMessage] = useState('Ready to Deploy');
  const [livePulse, setLivePulse] = useState(false);

  // Update status messages based on animation state
  useEffect(() => {
    if (isAnimating) {
      setStatusMessage('Fleet Active - San Diego Operations');
      setLivePulse(true);
    } else {
      setStatusMessage('Fleet Command Center - Ready to Deploy');
      setLivePulse(false);
    }
  }, [isAnimating]);

  // Enhanced status messages with more detail
  const getDetailedStatus = () => {
    if (isAnimating) {
      return {
        primary: 'FLEET DEPLOYED',
        secondary: 'Live Operations • San Diego County',
        tertiary: 'Zone SCF-921 • Real-time Tracking'
      };
    } else {
      return {
        primary: 'COMMAND CENTER',
        secondary: 'Ready for Operations',
        tertiary: `${new Date().toLocaleDateString()} • Standby Mode`
      };
    }
  };

  const status = getDetailedStatus();

  return (
    <div className="fleet-command-header">
      {/* Background with animated elements */}
      <div className="header-background">
        <div className="animated-grid" />
        <div className="gradient-overlay" />
      </div>

      {/* Main header content */}
      <div className="header-main">
        {/* Left section with title and status */}
        <div className="header-left">
          {/* Command center branding */}
          <div className="header-branding">
            <div className="command-icon">
              <Navigation className="navigation-icon" />
              <div className={`status-indicator ${livePulse ? 'active' : 'standby'}`} />
            </div>

            <div className="title-section">
              <h1 className="main-title">{status.primary}</h1>
              <div className="subtitle-section">
                <span className="subtitle-main">{status.secondary}</span>
                <div className="status-details">
                  <span className="detail-item">
                    <MapPin className="detail-icon" />
                    San Diego County
                  </span>
                  <span className="detail-item">
                    <Clock className="detail-icon" />
                    {currentTime.toLocaleTimeString()}
                  </span>
                  <span className="detail-item">
                    <Activity className="detail-icon" />
                    Zone SCF-921
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right section with controls */}
        <div className="header-right">
          {/* View controls */}
          <div className="view-controls">
            <button
              onClick={() => setMapMode(mapMode === 'satellite' ? 'street' : 'satellite')}
              className={`view-btn ${mapMode === 'satellite' ? 'active' : ''}`}
            >
              {mapMode === 'satellite' ? '🛰️ Satellite' : '🗺️ Street'}
            </button>

            <button
              onClick={() => setShowHeatmap(!showHeatmap)}
              className={`view-btn ${showHeatmap ? 'active' : ''}`}
            >
              🔥 Heat
            </button>
          </div>

          {/* Fleet control buttons */}
          <div className="fleet-controls">
            <button
              onClick={onStartAnimation}
              disabled={isAnimating}
              className="control-btn deploy-btn"
            >
              <Play className="control-icon" />
              <span>Deploy Fleet</span>
            </button>

            <button
              onClick={onPauseAnimation}
              disabled={!isAnimating}
              className="control-btn pause-btn"
            >
              <Pause className="control-icon" />
              <span>Hold</span>
            </button>

            <button
              onClick={onResetAnimation}
              className="control-btn reset-btn"
            >
              <RotateCcw className="control-icon" />
              <span>Reset</span>
            </button>
          </div>
        </div>
      </div>

      {/* Animated status bar */}
      <div className="status-bar">
        <div className="status-content">
          <div className="status-left">
            <div className="live-indicator">
              <div className={`pulse-dot ${livePulse ? 'pulsing' : ''}`} />
              <span className="status-text">{statusMessage}</span>
            </div>
          </div>

          <div className="status-right">
            <span className="system-info">
              USPS Fleet Management System v2.1
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetCommandHeader;
